import { changeLanguage } from 'i18next'
import type React from 'react'
import { lazy, Suspense, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import {
  AuthenticatedLayout,
  CHANGE_PASSWORD_PATH,
} from '@/domains/shared/components/AuthenticatedLayout'
import { LogoSplash } from '@/domains/shared/components/LogoSplash'
import { ProtectedRoute } from '@/domains/shared/components/ProtectedRoute'
import { RequirePermission } from '@/domains/shared/components/RequirePermission'
import { RequireSuperAdmin } from '@/domains/shared/components/RequireSuperAdmin'
import { LANGUAGES } from '@/domains/shared/constants'
import NotFoundPage from '@/domains/shared/routes/NotFoundPage'
import { useAuthStore } from '@/store/useAuthStore'

// The sign-in page is the only first-load module that uses forms; lazy, it keeps
// vendor-forms out of every first load, including direct visits to /pulse.
const UserSignIn = lazy(() => import('@/domains/shared/routes/UserSignIn'))
const DashboardPage = lazy(() => import('@/domains/dashboard/routes/DashboardPage'))
const UserSettingsPage = lazy(() => import('@/domains/settings/routes/UserSettingsPage'))
const UsersPage = lazy(() => import('@/domains/settings/routes/UsersPage'))
const RolesPage = lazy(() => import('@/domains/settings/routes/RolesPage'))
const RoleEditorPage = lazy(() => import('@/domains/settings/routes/RoleEditorPage'))
const CompaniesPage = lazy(() => import('@/domains/settings/routes/CompaniesPage'))
const ProductsPage = lazy(() => import('@/domains/products/routes/ProductsPage'))
const ProductCreatePage = lazy(() => import('@/domains/products/routes/ProductCreatePage'))
const ProductDetailLayout = lazy(() => import('@/domains/products/routes/ProductDetailLayout'))
const CategoriesPage = lazy(() => import('@/domains/categories/routes/CategoriesPage'))
const CategoryCreatePage = lazy(() => import('@/domains/categories/routes/CategoryCreatePage'))
const CategoryDetailPage = lazy(() => import('@/domains/categories/routes/CategoryDetailPage'))
const SuppliersPage = lazy(() => import('@/domains/suppliers/routes/SuppliersPage'))
const SupplierFormPage = lazy(() => import('@/domains/suppliers/routes/SupplierFormPage'))
const SupplierDetailLayout = lazy(() => import('@/domains/suppliers/routes/SupplierDetailLayout'))
const ChannelsPage = lazy(() => import('@/domains/channels/routes/ChannelsPage'))
const ChannelFormPage = lazy(() => import('@/domains/channels/routes/ChannelFormPage'))
const ChannelDetailLayout = lazy(() => import('@/domains/channels/routes/ChannelDetailLayout'))
const MediaPage = lazy(() => import('@/domains/media/routes/MediaPage'))
const RulesPage = lazy(() => import('@/domains/rules/routes/RulesPage'))
const PricingPage = lazy(() => import('@/domains/pricing/routes/PricingPage'))
const CompetitorPricesPage = lazy(
  () => import('@/domains/competitorPrices/routes/CompetitorPricesPage'),
)
const CampaignsPage = lazy(() => import('@/domains/campaigns/routes/CampaignsPage'))
const CampaignFormPage = lazy(() => import('@/domains/campaigns/routes/CampaignFormPage'))
const CampaignDetailLayout = lazy(() => import('@/domains/campaigns/routes/CampaignDetailLayout'))
const WarehousesPage = lazy(() => import('@/domains/warehouses/routes/WarehousesPage'))
const ErpSystemFormPage = lazy(() => import('@/domains/warehouses/routes/ErpSystemFormPage'))
const ErpSystemDetailLayout = lazy(
  () => import('@/domains/warehouses/routes/ErpSystemDetailLayout'),
)
const WarehouseFormPage = lazy(() => import('@/domains/warehouses/routes/WarehouseFormPage'))
const BulkRunsPage = lazy(() => import('@/domains/ai/routes/BulkRunsPage'))
const BulkRunDetailPage = lazy(() => import('@/domains/ai/routes/BulkRunDetailPage'))
const BulkRunWizard = lazy(() => import('@/domains/ai/routes/BulkRunWizard'))
const ImportsPage = lazy(() => import('@/domains/imports/routes/ImportsPage'))
const HelpPage = lazy(() => import('@/domains/help/routes/HelpPage'))
const ForcePasswordChangePage = lazy(
  () => import('@/domains/shared/routes/ForcePasswordChangePage'),
)

const App: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const hydrating = useAuthStore((s) => s.hydrating)
  const isAuthenticated = useAuthStore((s) => !!s.accessToken)
  const language = useAuthStore((s) => s.language)
  const { t } = useTranslation()

  // Redirect authenticated users away from the sign-in page (after hydration completes).
  // `location` comes from useLocation() and is reactive; pathname IS a valid dep here.
  // (React Doctor's no-mutable-in-deps assumes window.location and misfires.)
  useEffect(() => {
    if (!hydrating && location.pathname === '/' && isAuthenticated) {
      navigate('/pulse')
    }
  }, [hydrating, isAuthenticated, navigate, location.pathname])

  // Keep i18next in sync with the stored language preference. Hydration runs in
  // the background and may apply the user's server-side preference after mount,
  // so this must react to the store value, not read it once.
  useEffect(() => {
    changeLanguage(language || LANGUAGES.English)
  }, [language])

  return (
    <Routes>
      {/* Public */}
      <Route
        path="/"
        element={
          <Suspense fallback={<LogoSplash />}>
            <UserSignIn />
          </Suspense>
        }
      />

      {/* Authenticated shell */}
      <Route element={<ProtectedRoute />}>
        {/* Outside the shell: no sidebar until the temporary password is replaced */}
        <Route
          path={CHANGE_PASSWORD_PATH}
          element={
            <Suspense fallback={<div>{t('ui.loading')}</div>}>
              <ForcePasswordChangePage />
            </Suspense>
          }
        />
        <Route element={<AuthenticatedLayout />}>
          {/* Open to any signed-in user: the own profile and the help wiki */}
          <Route
            path="/settings"
            element={
              <Suspense fallback={<div>{t('ui.loading')}</div>}>
                <UserSettingsPage />
              </Suspense>
            }
          />
          <Route
            path="/help"
            element={
              <Suspense fallback={<div>{t('ui.loading')}</div>}>
                <HelpPage />
              </Suspense>
            }
          />
          {/* Every other route declares its action; the guard renders the Forbidden page without it */}
          <Route element={<RequirePermission action="dashboard.view" />}>
            <Route
              path="/pulse"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <DashboardPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="users.view" />}>
            <Route
              path="/settings/users"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <UsersPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="roles.view" />}>
            <Route
              path="/settings/roles"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <RolesPage />
                </Suspense>
              }
            />
            <Route
              path="/settings/roles/new"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <RoleEditorPage />
                </Suspense>
              }
            />
            <Route
              path="/settings/roles/:id"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <RoleEditorPage />
                </Suspense>
              }
            />
          </Route>
          {/* Not an action but the SuperAdmin grant: Inovio's tenant list */}
          <Route element={<RequireSuperAdmin />}>
            <Route
              path="/settings/companies"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <CompaniesPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="products.view" />}>
            <Route
              path="/products"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ProductsPage />
                </Suspense>
              }
            />
            <Route
              path="/products/:id"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ProductDetailLayout />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="products.create" />}>
            <Route
              path="/products/new"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ProductCreatePage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="categories.view" />}>
            <Route
              path="/categories"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <CategoriesPage />
                </Suspense>
              }
            />
            <Route
              path="/categories/:id"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <CategoryDetailPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="categories.manage" />}>
            <Route
              path="/categories/new"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <CategoryCreatePage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="suppliers.view" />}>
            <Route
              path="/suppliers"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <SuppliersPage />
                </Suspense>
              }
            />
            <Route
              path="/suppliers/:id"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <SupplierDetailLayout />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="suppliers.manage" />}>
            <Route
              path="/suppliers/new"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <SupplierFormPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action={['suppliers.import', 'products.create']} />}>
            <Route
              path="/imports"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ImportsPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="channels.view" />}>
            <Route
              path="/channels"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ChannelsPage />
                </Suspense>
              }
            />
            <Route
              path="/channels/:id"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ChannelDetailLayout />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="channels.manage" />}>
            <Route
              path="/channels/new"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ChannelFormPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="media.view" />}>
            <Route
              path="/media"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <MediaPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="pricing_rules.view" />}>
            <Route
              path="/pricing"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <PricingPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="channel_rules.view" />}>
            <Route
              path="/rules"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <RulesPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action={['competitor_prices.view', 'prices.view']} />}>
            <Route
              path="/competitor-prices"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <CompetitorPricesPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="campaigns.view" />}>
            <Route
              path="/campaigns"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <CampaignsPage />
                </Suspense>
              }
            />
            <Route
              path="/campaigns/:id"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <CampaignDetailLayout />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="campaigns.manage" />}>
            <Route
              path="/campaigns/new"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <CampaignFormPage />
                </Suspense>
              }
            />
            <Route
              path="/campaigns/:id/edit"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <CampaignFormPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="warehouses.view" />}>
            <Route
              path="/warehouses"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <WarehousesPage />
                </Suspense>
              }
            />
            <Route
              path="/warehouses/erp-systems/:id"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ErpSystemDetailLayout />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="warehouses.manage" />}>
            <Route
              path="/warehouses/erp-systems/new"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ErpSystemFormPage />
                </Suspense>
              }
            />
            <Route
              path="/warehouses/erp-systems/:id/edit"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <ErpSystemFormPage />
                </Suspense>
              }
            />
            <Route
              path="/warehouses/new"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <WarehouseFormPage />
                </Suspense>
              }
            />
            <Route
              path="/warehouses/:warehouseId/edit"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <WarehouseFormPage />
                </Suspense>
              }
            />
          </Route>
          <Route element={<RequirePermission action="ai.bulk_runs" />}>
            <Route
              path="/ai/bulk-runs"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <BulkRunsPage />
                </Suspense>
              }
            />
            <Route
              path="/ai/bulk-runs/new"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <BulkRunWizard />
                </Suspense>
              }
            />
            <Route
              path="/ai/bulk-runs/:id"
              element={
                <Suspense fallback={<div>{t('ui.loading')}</div>}>
                  <BulkRunDetailPage />
                </Suspense>
              }
            />
          </Route>
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default App
